/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type { FaxCandidate } from "../api/faxes";

/** One registry row a reviewer may pick, with the organization it belongs to. */
export type CandidateChoice = { id: number; label: string; organizationId: number | null };

function nested(candidate: FaxCandidate, key: string): Record<string, unknown> | null {
  const value = candidate[key];
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function numberOrNull(source: Record<string, unknown> | null, key: string): number | null {
  const value = source?.[key];
  return typeof value === "number" ? value : null;
}

function stringOrNull(source: Record<string, unknown> | null, key: string): string | null {
  const value = source?.[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/**
 * The resolver stores each candidate as its score and reasons wrapped around the matched row, so
 * the identity a reviewer picks lives one level down. Reading it off the wrapper silently yielded
 * no options at all, which is how a reviewer was left with "create new" as the only way forward.
 */
export function organizationChoices(candidates: FaxCandidate[] | null): CandidateChoice[] {
  return (candidates ?? []).flatMap((candidate) => {
    const organization = nested(candidate, "organization");
    const id = numberOrNull(organization, "id");
    if (id === null) {
      return [];
    }
    return [{ id, label: stringOrNull(organization, "name") ?? `Organization ${id}`, organizationId: id }];
  });
}

export function providerChoices(candidates: FaxCandidate[] | null): CandidateChoice[] {
  return (candidates ?? []).flatMap((candidate) => {
    const provider = nested(candidate, "provider");
    const id = numberOrNull(provider, "id");
    if (id === null) {
      return [];
    }
    const contact = provider === null ? null : nested(provider, "contact");
    const name = [stringOrNull(contact, "firstName"), stringOrNull(contact, "lastName")]
      .filter((part) => part !== null)
      .join(" ");
    return [
      {
        id,
        label: name === "" ? `Provider ${id}` : name,
        organizationId: numberOrNull(provider, "referringOrganizationId"),
      },
    ];
  });
}

/**
 * A provider may only be filed under the organization they already belong to. The API refuses
 * anything else, and an organization that does not exist yet can have no existing providers.
 */
export function providersForOrganization(
  choices: CandidateChoice[],
  organizationId: number | null,
): CandidateChoice[] {
  return organizationId === null
    ? []
    : choices.filter((choice) => choice.organizationId === organizationId);
}
